import { apiError } from "../../../../../../../../utils/formErrors";
import LetterHistory from "../../../../../../../../component/letters/LetterHistory";
import { getLegalUnitList } from "../../../../../../../../services/globalData";
import React, { useEffect, useState, useMemo } from "react";
import { Box, Stack, Select, MenuItem } from "@mui/material";
import { useOutletContext, useNavigate } from "react-router";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { getAllDayAttendanceByEmployeeId } from "../../../../../../../../services/facialRecognition";
import { getLeaveBalanceByEmployee, getLeaveRequestsByEmployee } from "../../../../../../../../services/leave";
import { getAllExpense } from "../../../../../../../../services/expense";
import { getAllTimesheets } from "../../../../../../../../services/timesheet";
import { getOrgDepartments } from "../../../../../../../../services/globalData";
import { updateEmployeeDetailsById } from "../../../../../../../../services/employee";
import { useNotify } from "../../../../../../../../component/NotificationProvider";
import { tokens, displayFont, motion, radius } from "../../../../../../../../theme";

import { pressable } from "../../../../../../../../component/pressableProps";
import AttendancePolicyFact from "./AttendancePolicyFact";
const money = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN")}`;
const thisMonth = () => { const d = new Date(); return { m: d.getUTCMonth(), y: d.getUTCFullYear() }; };

const SnapshotCard = ({ icon, label, value, sub, tone, onClick }) => (
  <Box {...pressable(onClick)}
    sx={{ flex: 1, minWidth: "13rem", background: tokens.card, border: `1px solid ${tokens.border}`, borderRadius: `${radius.card}px`,
      p: "1.25rem", cursor: onClick ? "pointer" : "default", transition: `border-color 120ms ${motion.easing.standard}`,
      "&:hover": onClick ? { borderColor: `${tone}88` } : {} }}>
    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: "0.75rem" }}>
      <Box sx={{ width: "2.25rem", height: "2.25rem", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", background: `${tone}14`, color: tone }}>{icon}</Box>
      <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, fontWeight: 500 }}>{label}</Box>
    </Stack>
    <Box sx={{ fontFamily: displayFont, fontSize: "1.75rem", fontWeight: 700, color: tokens.text, lineHeight: 1.2 }}>{value}</Box>
    {sub != null && <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mt: "0.5rem" }}>{sub}</Box>}
  </Box>
);

const Fact = ({ label, value }) => (
  <Box>
    <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, textTransform: "uppercase", letterSpacing: "0.03em" }}>{label}</Box>
    <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, color: tokens.text, mt: "0.25rem" }}>{value || "—"}</Box>
  </Box>
);

const ProfileOverview = () => {
  const [employee, employeeId] = useOutletContext();
  const navigate = useNavigate();
  const notify = useNotify();
  const [geofenceExempt, setGeofenceExempt] = useState(employee?.geofenceExempt || "inherit");

  useEffect(() => { setGeofenceExempt(employee?.geofenceExempt || "inherit"); }, [employee]);

  const saveGeofenceExempt = async (value) => {
    const prev = geofenceExempt;
    setGeofenceExempt(value);
    try {
      const res = await updateEmployeeDetailsById(employeeId, { geofenceExempt: value });
      if (res?.success === false) throw apiError(res, "Update failed");
      notify.success("Geofence setting updated");
    } catch (e) {
      setGeofenceExempt(prev);
      notify.error(e?.message || "Could not update geofence setting");
    }
  };
  const [att, setAtt] = useState([]);
  const [balance, setBalance] = useState([]);
  const [leaveReqs, setLeaveReqs] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [timesheets, setTimesheets] = useState([]);

  useEffect(() => {
    if (!employeeId) return;
    let active = true;
    (async () => {
      const [a, b, lr, ex, ts] = await Promise.all([
        getAllDayAttendanceByEmployeeId(employeeId).catch(() => null),
        getLeaveBalanceByEmployee(employeeId).catch(() => null),
        getLeaveRequestsByEmployee(employeeId).catch(() => null),
        getAllExpense().catch(() => null),
        getAllTimesheets().catch(() => null),
      ]);
      if (!active) return;
      setAtt(Array.isArray(a?.data) ? a.data : []);
      setBalance(Array.isArray(b?.data) ? b.data : []);
      setLeaveReqs(Array.isArray(lr?.data) ? lr.data : []);
      setExpenses((Array.isArray(ex?.data) ? ex.data : []).filter((e) => e.submitterId === employeeId && e.type === "ANY"));
      setTimesheets((Array.isArray(ts?.data) ? ts.data : []).filter((t) => t.employee === employeeId));
    })();
    return () => { active = false; };
  }, [employeeId]);

  const stats = useMemo(() => {
    const { m, y } = thisMonth();
    const presentThisMonth = att.filter((r) => {
      const d = new Date(r.dayString || r.date);
      return d.getUTCMonth() === m && d.getUTCFullYear() === y && r.arrivalTime != null;
    }).length;
    return {
      presentThisMonth,
      leaveBalance: balance.reduce((s, b) => s + (b.quantity || 0), 0),
      leavePending: leaveReqs.filter((r) => r.status === "Pending").length,
      expensePending: expenses.filter((e) => e.status === "pending").length,
      expensePendingAmt: expenses.filter((e) => e.status === "pending").reduce((s, e) => s + Number(e.amount || 0), 0),
      tsPending: timesheets.filter((t) => t.status === "pending").length,
    };
  }, [att, balance, leaveReqs, expenses, timesheets]);

  const deptName = (getOrgDepartments() || []).find((d) => d?._id === employee?.department)?.name;

  /**
   * `Employee.location` holds a LEGAL ENTITY id, not a location.
   *
   * The onboarding form that writes it is labelled "Legal entity" and picks
   * from the legal-unit list; the field kept an older name. Printed raw it
   * showed "6a9834e7f7d6d004736ff90b" where a company name belongs — and the
   * `locations` collection is empty, so there was never anything else it could
   * have meant. Labelled for what it is, rather than resolved under a name
   * that would send the next reader looking for a branch office.
   */
  const entityName = (getLegalUnitList() || []).find(
    (u) => String(u?._id) === String(employee?.location)
  )?.name;
  const base = `/app/employee-details/${employeeId}`;

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: "1.5rem", flexWrap: "wrap", rowGap: 2 }}>
        <SnapshotCard icon={<AccessTimeOutlinedIcon fontSize="small" />} label="Present this month" value={stats.presentThisMonth}
          sub="View attendance →" tone={tokens.accent} onClick={() => navigate(`${base}/attendance`)} />
        <SnapshotCard icon={<EventAvailableOutlinedIcon fontSize="small" />} label="Leave balance" value={stats.leaveBalance}
          sub={`${stats.leavePending} pending request(s)`} tone={tokens.success} onClick={() => navigate(`${base}/leave`)} />
        <SnapshotCard icon={<ReceiptLongOutlinedIcon fontSize="small" />} label="Pending expenses" value={money(stats.expensePendingAmt)}
          sub={`${stats.expensePending} claim(s) to review`} tone={tokens.warning} onClick={() => navigate(`${base}/expenses`)} />
        <SnapshotCard icon={<ScheduleOutlinedIcon fontSize="small" />} label="Timesheets" value={stats.tsPending}
          sub="pending approval" tone={tokens.danger} onClick={() => navigate(`${base}/timesheets`)} />
      </Stack>

      <Stack direction={{ xs: "column", lg: "row" }} spacing={2} alignItems="flex-start">
        <Box sx={{ flex: 1, width: "100%", background: tokens.card, border: `1px solid ${tokens.border}`, borderRadius: `${radius.card}px`, p: "1.5rem" }}>
          <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, mb: "1.25rem" }}>Job details</Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: "1.25rem" }}>
            <Fact label="Designation" value={employee?.designation} />
            <Fact label="Department" value={deptName} />
            <Fact label="Employment Type" value={employee?.employmentType} />
            <Fact label="Date of Joining" value={employee?.joiningDate?.split("T")[0]} />
            <Fact label="Legal entity" value={entityName} />
            <Fact label="Grade" value={employee?.grade} />
            <AttendancePolicyFact employeeId={employeeId} />
            <Box>
              <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, textTransform: "uppercase", letterSpacing: "0.03em", mb: "0.25rem" }}>Geofence</Box>
              <Select size="small" fullWidth value={geofenceExempt} onChange={(e) => saveGeofenceExempt(e.target.value)}
                sx={{ fontSize: "0.875rem", "& .MuiSelect-select": { py: "0.5rem" } }}>
                <MenuItem value="inherit">Inherit (designation default)</MenuItem>
                <MenuItem value="exempt">Exempt — no geofence (field staff / WFH)</MenuItem>
                <MenuItem value="enforce">Always enforce</MenuItem>
              </Select>
            </Box>
          </Box>
        </Box>

        <Box sx={{ flex: 1, width: "100%", background: tokens.card, border: `1px solid ${tokens.border}`, borderRadius: `${radius.card}px`, p: "1.5rem" }}>
          <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, mb: "1.25rem" }}>Contact</Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: "1.25rem" }}>
            <Fact label="Work Email" value={employee?.email} />
            <Fact label="Phone" value={employee?.phone} />
            <Fact label="Present Address" value={employee?.presentStreet} />
            <Fact label="Emergency Contact" value={employee?.emergencyContactName} />
          </Box>
          <Box {...pressable(() => navigate(`${base}/profile`))}
            sx={{ mt: "1.25rem", fontSize: "0.8125rem", fontWeight: 600, color: tokens.accent, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
            Full profile <ArrowForwardIcon sx={{ fontSize: "0.875rem" }} />
          </Box>
        </Box>
      </Stack>

      <Box sx={{ background: tokens.card, border: `1px solid ${tokens.border}`, borderRadius: `${radius.card}px`, p: "1.5rem", mt: "1rem" }}>
        <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, mb: "0.25rem" }}>Documents</Box>
        {/* Letters issued to this person. Not a file store — each is
            regenerated from what was recorded when it was issued. */}
        <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mb: "1.25rem" }}>
          Issue one from the ⋯ menu at the top of this page.
        </Box>
        <LetterHistory employeeId={employeeId} />
      </Box>
    </Box>
  );
};

export default ProfileOverview;